import _ from 'lodash'
import { intl } from '../../../../i18n'
import { allDescendants, focusRow, getSelectedNode } from '../lib/gridApi'
import { TreeRow } from '../model'
import { copyRows } from '../../commons/AgGrid/copiedRowFrame'
import {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  GridApi,
  IRowNode,
  MenuItemDef,
} from 'ag-grid-community'
import { moveRowsToLastChild } from '../hooks/actions/moveTreeRows'
import {
  ContextMenuItemId,
  getMenuIconHtml,
} from '../../commons/AgGrid/lib/contextMenu'
import { WbsItemType } from '../../../../domain/entity/WbsItemEntity'
import { getPathByExternalId } from '../../../pages'
import { getExternalIdByWbsItem } from '../../../../lib/functions/wbsItem'
import ReactDOMServer from 'react-dom/server'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import { AiAgentWbsItemRef } from '../../../../lib/functions/ai'
import { attachWbsItemsToAiAgentChat } from '../../../features/AiAgentChat/attachWbsItemToChat'

export interface WbsItem {
  code?: string
  ticketType?: string
  type: WbsItemType
  displayName?: string
}

export const addCopyURLMenuItems = (
  wbsItems: WbsItem[] | undefined
): MenuItemDef => {
  return {
    name: intl.formatMessage({
      id: 'bulksheet.contextMenu.url.copy',
    }),
    action: () => {
      if (!wbsItems || wbsItems.length === 0) return
      return copyURLCore(wbsItems, getURL)
    },
    icon: getMenuIconHtml(ContextMenuItemId.COPY_URL),
  }
}

export const addCopyURLAndNameMenuItems = (
  wbsItems: WbsItem[] | undefined
): MenuItemDef => {
  return {
    name: intl.formatMessage({
      id: 'bulksheet.contextMenu.urlAndName.copy',
    }),
    action: () => {
      if (!wbsItems || wbsItems.length === 0) return
      return copyURLCore(wbsItems, getURLAndName)
    },
    icon: getMenuIconHtml(ContextMenuItemId.COPY_URL_AND_NAME),
  }
}

export const addAttachToAiAgentChatMenuItems = (
  wbsItems: WbsItem[] | undefined
): MenuItemDef => {
  return {
    name: intl.formatMessage({
      id: 'aiAgentChat.attach.wbsItem',
    }),
    // AIの応答に添えているものと同じアイコンを使う。assets配下に該当アイコンが
    // 無いため、MUIのアイコンをhtmlへ変換して渡している。
    // 寸法と色は他のメニューアイコン（20px・#888888）に合わせ、揃える。
    icon: ReactDOMServer.renderToStaticMarkup(
      <SmartToyIcon
        style={{ width: '20px', height: '20px', fill: '#888888' }}
      />
    ),
    action: () => attachToAiAgentChatCore(wbsItems),
  }
}

// 実体（description等）は送らず、code/name/typeのみをAIチャットへ添付する
// （エージェント側は既存APIで実体を取得する設計のため）。
export const attachToAiAgentChatCore = (wbsItems: WbsItem[] | undefined) => {
  if (!wbsItems || wbsItems.length === 0) return
  const refs: AiAgentWbsItemRef[] = wbsItems
    .filter(
      (item): item is WbsItem & { code: string; displayName: string } =>
        !!item.code && !!item.displayName
    )
    .map(item => ({
      code: item.code,
      name: item.displayName,
      type: item.type,
    }))
  attachWbsItemsToAiAgentChat(refs)
}

export const copyURLCore = (
  wbsItems: WbsItem[] | undefined,
  getCopyText: (path: string, wbsItem: WbsItem) => string
) => {
  if (!wbsItems || wbsItems.length === 0) return

  const resultTextArray = wbsItems.map(item => {
    const externalId = getExternalIdByWbsItem(item)
    const path = getPathByExternalId(externalId)
    return getCopyText(path, item)
  })
  navigator.clipboard.writeText(resultTextArray.join('\n'))
}

export const getURL = (
  path: string,
  wbsItem: { code?: string; ticketType?: string; type: WbsItemType }
) => {
  return `${window.location.origin}${path}/${wbsItem.code}`
}

export const getURLAndName = (path: string, wbsItem: WbsItem) => {
  return `${wbsItem.displayName}\n${window.location.origin}${path}/${wbsItem.code}`
}

export const focusCellAfterCopy = (api: GridApi) => {
  setTimeout(() => {
    const focusedCell = api.getFocusedCell()
    focusedCell && api.setFocusedCell(focusedCell.rowIndex, focusedCell.column)
  }, 100)
}

export const copyTreeMenuItems = <R extends TreeRow>(
  params: GetContextMenuItemsParams,
  data: R[]
): MenuItemDef => {
  const currentCopiedNodes =
    params.context.copied?.map(v => params.api.getRowNode(v.id)) ?? []
  const selectedNodes = getSelectedNode(params.api)
  return {
    name: intl.formatMessage(
      { id: 'bulksheet.contextMenu.copy' },
      { count: selectedNodes.length }
    ),
    icon: getMenuIconHtml(ContextMenuItemId.COPY_ROW),
    disabled: false,
    action: () => {
      params.context.copied = copyRows(
        data,
        selectedNodes.map(node => node.data)
      )
      params.api.redrawRows({
        rowNodes: [...currentCopiedNodes, ...selectedNodes].filter(v => !!v),
      })
      focusCellAfterCopy(params.api)
    },
    shortcut: intl.formatMessage({
      id: 'bulksheet.contextMenu.shortcut.row.copy',
    }),
  }
}

export const copyAllTreeMenuItems = <R extends TreeRow>(
  params: GetContextMenuItemsParams,
  data: R[],
  action: {
    afterCopy: (uuids: string[]) => void
  }
): MenuItemDef => {
  return {
    name: intl.formatMessage({ id: 'bulksheet.contextMenu.bulkCopy' }),
    action: () => {
      const { node, context } = params
      if (!node) return
      const target = allDescendants(node)
        .map(v => v.data)
        .filter(v => v)
      context.copied = copyRows(data, target, false)
      action.afterCopy(target.map(v => v?.uuid).filter(v => v) as string[])
    },
  }
}

export const cutTreeMenuItems = <R extends TreeRow>(
  params: GetContextMenuItemsParams,
  data: R[]
): MenuItemDef => {
  const currentCopiedNodes =
    params.context.copied?.map(v => params.api.getRowNode(v.id)) ?? []
  const selectedNodes = getSelectedNode(params.api)
  return {
    name: intl.formatMessage(
      { id: 'bulksheet.contextMenu.cut' },
      { count: selectedNodes.length }
    ),
    icon: getMenuIconHtml(ContextMenuItemId.CUT_ROW),
    disabled: false,
    action: () => {
      params.context.copied = copyRows(
        data,
        selectedNodes.map(node => node.data),
        true
      )
      params.api.redrawRows({
        rowNodes: [...currentCopiedNodes, ...selectedNodes].filter(v => !!v),
      })
      focusCellAfterCopy(params.api)
    },
    shortcut: intl.formatMessage({
      id: 'bulksheet.contextMenu.shortcut.cut.row',
    }),
  }
}

export const pasteTreeMenuItems = <R extends TreeRow>(
  params: GetContextMenuItemsParams,
  action: {
    pasteNameOnly: () => void
    selectColumns: () => void
  }
): MenuItemDef => {
  const selectedNodes = getSelectedNode(params.api)
  return {
    name: intl.formatMessage(
      { id: 'bulksheet.contextMenu.paste.children' },
      {
        clipboard: params.context.copied?.length ?? 0,
        selected: selectedNodes.length,
      }
    ),
    icon: getMenuIconHtml(ContextMenuItemId.PASTE_ROW_AS_CHILD),
    subMenu: [
      {
        name: intl.formatMessage({
          id: 'bulksheet.contextMenu.paste.rows.normal',
        }),
        action: () => action.pasteNameOnly(),
        shortcut: intl.formatMessage({
          id: 'bulksheet.contextMenu.shortcut.row.paste.name',
        }),
      },
      {
        name: intl.formatMessage({
          id: 'bulksheet.contextMenu.paste.rows.selectedCol',
        }),
        action: () => {
          action.selectColumns()
        },
        shortcut: intl.formatMessage({
          id: 'bulksheet.contextMenu.shortcut.row.paste.data',
        }),
      },
    ],
  }
}

export const pasteCutTreeMenuItems = <R extends TreeRow>(
  params: GetContextMenuItemsParams,
  data: R[],
  action: {
    duplicateRow: (row: R) => R
    setData: (data: R[], moved: R[]) => void
  }
): MenuItemDef => {
  return {
    name: intl.formatMessage(
      { id: 'bulksheet.contextMenu.insert.cutrows' },
      { cutrows: params.context.copied?.length ?? 0 }
    ),
    icon: getMenuIconHtml(ContextMenuItemId.INSERT_CUT_ROW),
    disabled: (params.context.copied?.length ?? 0) === 0,
    action: () => {
      const overNode = params.node
      const movedData = params.context.copied
        .map(v => data.find(d => d.uuid === v.id))
        .filter(v => !!v)
      if (!overNode || _.isEmpty(movedData)) return
      action.setData(
        moveRowsToLastChild(data, movedData, overNode.id),
        movedData
      )
      overNode.setExpanded(true)
      focusRow(params.api, movedData[0].uuid)
    },
    shortcut: intl.formatMessage({
      id: 'bulksheet.contextMenu.shortcut.cut.row.paste',
    }),
  }
}

export const groupRowMenuItems = (
  params: GetContextMenuItemsParams
): (DefaultMenuItem | MenuItemDef)[] => {
  const selectedNodes = getSelectedNode(params.api)
  if (selectedNodes.length === 0) return []

  if (
    selectedNodes.some(
      node => node.childrenAfterGroup && node.childrenAfterGroup.length > 0
    )
  ) {
    return [
      'separator',
      expandAllMenu(params),
      collapseAllMenu(params),
      expandToLevelMenu(params),
    ]
  }
  return []
}

export const expandAllMenu = (
  params: GetContextMenuItemsParams
): MenuItemDef => {
  return {
    name: intl.formatMessage({
      id: 'bulksheet.contextMenu.expand.allLeafChildren',
    }),
    icon: getMenuIconHtml(ContextMenuItemId.EXPAND_ALL),
    action: () => {
      getSelectedNode(params.api)
        .flatMap(node => allDescendants(node))
        .filter(child => child.isExpandable() && !child.expanded)
        .forEach(child => child.setExpanded(true))
    },
  }
}

export const collapseAllMenu = (
  params: GetContextMenuItemsParams
): MenuItemDef => {
  return {
    name: intl.formatMessage({
      id: 'bulksheet.contextMenu.collapse.allLeafChildren',
    }),
    icon: getMenuIconHtml(ContextMenuItemId.COLLAPSE_ALL),
    action: () => {
      getSelectedNode(params.api)
        .flatMap(node => allDescendants(node))
        .filter(child => child.isExpandable() && child.expanded)
        .forEach(child => child.setExpanded(false))
    },
  }
}

const collapseNode = (node: IRowNode) => {
  allDescendants(node).forEach(child => {
    if (child.isExpandable() && child.expanded) {
      child.setExpanded(false)
    }
  })
}

const expandNode = (node: IRowNode) => {
  if (node.isExpandable() && !node.expanded) {
    node.setExpanded(true)
  }
}

const expandRecursive = (
  node: IRowNode,
  currentLevel: number,
  inputLevel: number
) => {
  if (currentLevel < inputLevel) {
    expandNode(node)
    node.childrenAfterGroup?.forEach((childNode: IRowNode) => {
      expandRecursive(childNode, currentLevel + 1, inputLevel)
    })
  } else {
    collapseNode(node)
  }
}

const ExpandToLevelSubMenuAction = (
  params: GetContextMenuItemsParams,
  inputLevel: number
) => {
  const selectedNodes = getSelectedNode(params.api)
  if (selectedNodes.length === 0) return
  selectedNodes.forEach(node => {
    const currentLevel: 0 = 0
    expandRecursive(node, currentLevel, inputLevel)
  })
}
export const expandToLevelMenu = (
  params: GetContextMenuItemsParams
): MenuItemDef => {
  return {
    name: intl.formatMessage({
      id: 'bulksheet.contextMenu.expandToLevel.leafChildren',
    }),
    icon: getMenuIconHtml(ContextMenuItemId.EXPAND_TO_LEVEL_MENU),
    subMenu: Array.from({ length: 5 }, (_, index) => ({
      name: intl.formatMessage(
        {
          id: 'bulksheet.contextMenu.expandToLevel.subMenu',
        },
        { level: (index + 1).toString() }
      ),
      action: () => {
        ExpandToLevelSubMenuAction(params, index + 1)
      },
    })),
  }
}

export const expandAllRows = (
  params: GetContextMenuItemsParams
): MenuItemDef => {
  return {
    name: intl.formatMessage({ id: 'bulksheet.contextMenu.expand.all' }),
    icon: getMenuIconHtml(ContextMenuItemId.EXPAND_ALL),
    disabled: params.api.getRenderedNodes().length <= 0,
    action: () => {
      params.api.forEachNode(node => {
        allDescendants(node).forEach(child => {
          if (child.isExpandable() && !child.expanded) {
            child.setExpanded(true)
          }
        })
      })
    },
  }
}

export const collapseAllRows = (
  params: GetContextMenuItemsParams
): MenuItemDef => {
  return {
    name: intl.formatMessage({ id: 'bulksheet.contextMenu.collapse.all' }),
    icon: getMenuIconHtml(ContextMenuItemId.COLLAPSE_ALL),
    disabled: params.api.getRenderedNodes().length <= 0,
    action: () => {
      params.api.forEachNode(node => {
        allDescendants(node).forEach(child => {
          if (child.isExpandable() && child.expanded) {
            child.setExpanded(false)
          }
        })
      })
    },
  }
}
